import React, {useEffect} from 'react';

import {useHistory, useParams} from 'react-router-dom';

import Login from './components/pages/auth/login';
import LoginHelp from './components/pages/auth/loginHelp';
import ForgotPassword from './components/pages/auth/forgotPassword';
import ForgotPasswordConfirmation from './components/pages/auth/forgotPasswordConfirmation';
import PasswordLinkExpired from './components/pages/auth/PasswordLinkExpired';
import ChangePassword from './components/pages/auth/changePassword';
import Browse from './components/pages/browse';
import CoachAI from './components/pages/coach-ai';
import Home from './components/pages/home';
import Profile from './components/pages/profile';
import Playlist from './components/pages/playlist';
import Search from './components/pages/search';
import UserSettings from './components/pages/userSettings';
import Inbox from './components/pages/inbox';
import Unauthorised from './components/pages/errors/Unauthorised';
import {DEFAULT_EDIT_PLAYLIST_ROUTES} from 'screens/SinglePlaylistManager';
import {COACH_AI_ROUTE, PLATFORM_ANNOUNCEMENTS_ROUTE} from 'utils/routes';

const MyTeam = React.lazy(
	() => import(/* webpackChunkName: "myteam" */ './components/pages/myTeam')
);
const MyTeams = React.lazy(
	() => import(/* webpackChunkName: "myteams" */ './components/pages/myTeams')
);
const MyTeamMember = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteammember" */ './components/pages/myTeam/people/member'
		)
);
const MyPlaylistStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists'
		)
);
const UserProgressAgainstPlaylist = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/playlists/userPlaylistProgress'
		)
);
const MyAssetStats = React.lazy(
	() =>
		import(
			/* webpackChunkName: "myteam" */ './components/pages/myTeam/assets'
		)
);
const PlaylistEditorPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "playlist-editor" */ './components/pages/playlist/PlaylistEditor'
		)
);

const PlatformAnnouncementsPage = React.lazy(
	() =>
		import(
			/* webpackChunkName: "platform-announcements" */ './components/pages/platform-announcements'
		)
);

// At some point it would probably make sense to convert our flat
// route config to a more conventional nested structure. Probably
// in conjunction with an update to React Router 6 or React Location.

const publicRoutes = [
	{
		path: ['/', '/login'],
		component: Login,
		exact: true,
	},
	{
		path: '/login-help',
		component: LoginHelp,
		exact: true,
	},
	{
		path: '/forgot',
		component: ForgotPassword,
		exact: true,
	},
	{
		path: '/password-requested',
		component: ForgotPasswordConfirmation,
		exact: true,
	},
	{
		path: '/password-link-expired',
		component: PasswordLinkExpired,
		exact: true,
	},
	{
		path: '/change-password',
		component: ChangePassword,
		exact: true,
	},
	{
		path: '/unauthorised',
		component: Unauthorised,
		exact: true,
	},
];

const privateRoutes = [
	{
		path: '/home',
		component: Home,
		exact: true,
	},
	{
		path: [
			'/profile',
			'/profile/playlists',
			'/profile/assets',
			'/profile/badges',
			// legacy routes
			'/profile/favourites',
		],
		component: Profile,
	},
	{
		path: ['/browse', '/browse/:topicId'],
		component: Browse,
		exact: true,
	},
	{
		path: '/search',
		component: Search,
		exact: true,
	},
	{
		path: '/settings',
		component: UserSettings,
		exact: true,
	},
	{
		path: '/inbox',
		component: Inbox,
		exact: true,
	},
	{
		path: COACH_AI_ROUTE,
		component: CoachAI,
		exact: true,
	},
	{
		path: PLATFORM_ANNOUNCEMENTS_ROUTE,
		component: PlatformAnnouncementsPage,
		exact: true,
	},
	{
		path: '/my-teams',
		component: MyTeams,
		exact: true,
	},
	{
		path: ['/my-teams/:teamId/people/:userId', '/my-teams/people/:userId'],
		component: MyTeamMember,
		exact: true,
	},
	{
		path: [
			'/playlists/:playlistId/analytics',
			'/playlists/:playlistId/analytics/:teamId',
		],
		component: MyPlaylistStats,
		exact: true,
	},
	{
		path: [
			'/assets/:assetId/analytics',
			'/assets/:assetId/analytics/:teamId',
		],
		component: MyAssetStats,
		exact: true,
	},
	{
		path: '/user/:userId/playlist/:playlistId',
		component: UserProgressAgainstPlaylist,
		exact: true,
	},
	{
		path: [
			'/my-teams/:teamId',
			'/my-teams/:teamId/trends',
			'/my-teams/:teamId/people',
			'/my-teams/:teamId/reports',
			'/my-teams/:teamId/reports/:reportId',
			'/my-teams/:teamId/playlists',
		],
		component: MyTeam,
	},
	{
		path: [
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistDetails,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContent,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistContentPreview,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistShare,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAccess,
			DEFAULT_EDIT_PLAYLIST_ROUTES.singlePlaylistAnalytics,
		],
		component: PlaylistEditorPage,
	},
	{
		path: ['/playlists/:id', '/playlists/:id/comments'],
		component: Playlist,
	},
	{
		path: '/assets/:id',
		component: RedirectAsset,
		exact: true,
	},
];

// This component is a workaround for a limitation in React Router:
// Normally we'd use a regular Redirect component, but that doesn't
// allow mapping URL parameters to a query string _while also_
// attaching location state.
function RedirectAsset() {
	const history = useHistory();
	const {id} = useParams();
	useEffect(() => {
		history.replace({
			pathname: '/home',
			search: `?asset=${id}`,
			state: {
				wasRedirected: true,
			},
		});
	});
	return null;
}

const publicRoutePaths = publicRoutes
	.flatMap(route => route.path)
	.filter(Boolean);

export {publicRoutes, privateRoutes, publicRoutePaths};
